import { cn } from "@/lib";
import { ArrowRightIcon } from "lucide-react";
import Container from "../global/container";
import Icons from "../global/icons";
import Images from "../global/images";
import { Button } from "../ui/button";
import Ripple from "../ui/ripple";

const SOCIAL_PLATFORMS = [
  {
    icon: Icons.linkedin,
    position: "left-3",
    size: "small",
    iconSize: "small",
    className: "hidden lg:flex",
  },
  {
    icon: Icons.tiktok,
    position: "left-2",
    size: "medium",
    iconSize: "medium",
  },
  { icon: Icons.insta, position: "left-1", size: "large", iconSize: "large" },
  {
    icon: Icons.youtube,
    position: "right-1",
    size: "large",
    iconSize: "large",
  },
  { icon: Icons.x, position: "right-2", size: "medium", iconSize: "medium" },
  {
    icon: Icons.facebook,
    position: "right-3",
    size: "small",
    iconSize: "small",
    className: "hidden lg:flex",
  },
];

const Integration = () => {
  const getPositionClasses = (position: string) => {
    switch (position) {
      case "left-3":
        return "-translate-x-[285px]";
      case "left-2":
        return "-translate-x-[210px]";
      case "left-1":
        return "-translate-x-[125px]";
      case "right-1":
        return "translate-x-[125px]";
      case "right-2":
        return "translate-x-[210px]";
      case "right-3":
        return "translate-x-[285px]";
      default:
        return "";
    }
  };

  const getSizeClasses = (size: string) => {
    switch (size) {
      case "large":
        return "size-20";
      case "medium":
        return "size-16";
      case "small":
        return "size-12";
      default:
        return "size-20";
    }
  };

  const getIconSizeClasses = (size: string) => {
    switch (size) {
      case "large":
        return "size-10";
      case "medium":
        return "size-7";
      case "small":
        return "size-5";
      default:
        return "size-10";
    }
  };

  return (
    <div
      className="relative flex flex-col items-center justify-center w-full py-20 scale-"
      data-oid="ly:nqxw"
    >
      <Container className="relative" data-oid="y5nt415">
        <div
          className="relative flex flex-col lg:hidden items-center justify-center overflow-visible"
          data-oid="e3nwvdd"
        >
          <div
            className="absolute top-1/2 -translate-y-1/2 right-1/4 w-3/5 h-14 lg:h-20 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full -rotate-12 blur-[6.5rem] -z-10"
            data-oid="5wii6a6"
          ></div>

          <div
            className="max-w-sm w-full h-auto mx-auto mt-8"
            data-oid="tyt5am7"
          >
            <img
              src="/images/integration.svg"
              alt="Integration"
              className="w-full h-auto"
              data-oid="zlx3l0p"
            />
          </div>
        </div>
      </Container>

      <div
        className="flex flex-col items-center text-center max-w-3xl mx-auto lg:absolute lg:top-1/4 inset-x-0 mt-12 lg:mt-0"
        data-oid="x1qphji"
      >
        <h2
          className="text-2xl md:text-4xl lg:text-6xl font-heading font-semibold !leading-snug"
          data-oid="473d3fi"
        >
          Social Media Integration
        </h2>
      </div>
      <div
        className="flex flex-col items-center text-center max-w-3xl mx-auto lg:absolute lg:bottom-1/4 inset-x-0 z-20 mt-8 lg:mt-0"
        data-oid="m68iut6"
      >
        <div
          className="relative group scale-100 md:scale-110 lg:scale-125"
          data-oid="xzluyz5"
        >
          <Button
            size="lg"
            className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white hover:shadow-lg transition-all duration-300 px-4 sm:px-6 md:px-8 py-4 md:py-6 text-base md:text-xl"
            data-oid="ch5kmrw"
          >
            <span
              className="animate-pulse text-lg md:text-2xl"
              data-oid="ra68o7f"
            >
              ✨
            </span>
            <span className="mx-2 md:mx-3 font-semibold" data-oid="d_sj_8h">
              Coming Soon
            </span>
            <span
              className="animate-pulse text-lg md:text-2xl"
              data-oid="o_5hi3e"
            >
              ✨
            </span>
          </Button>
          <div
            className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-lg blur opacity-30 group-hover:opacity-50 transition duration-300"
            data-oid="fh2_7qo"
          ></div>
        </div>
      </div>

      <Container delay={0.3} data-oid="wk:5ybw">
        <div
          className="relative hidden lg:flex items-center justify-center overflow-visible"
          data-oid="dd8is2f"
        >
          <div
            className="absolute top-1/2 -translate-y-1/2 right-1/4 w-3/5 h-14 lg:h-20 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full -rotate-12 blur-[6.5rem] -z-10"
            data-oid="mya28ce"
          ></div>

          <div
            className="relative flex h-dvh w-full flex-col items-center justify-center overflow-visible"
            data-oid="nzsgp_i"
          >
            <Ripple data-oid="oew674l" />
          </div>

          <div
            className="absolute z-20 flex items-center justify-center group"
            data-oid="ersz2gn"
          >
            <Images.logo
              className="size-24 group-hover:scale-110 transition-all duration-500"
              data-oid="7:cgrb3"
            />
          </div>

          {SOCIAL_PLATFORMS.map((platform, index) => (
            <div
              key={index}
              className={cn(
                "absolute z-20 size-16 p-3 rounded-full flex items-center justify-center bg-gradient-to-b from-foreground/5 to-transparent shadow-xl shadow-black/10 backdrop-blur-lg transition-all duration-300 hover:scale-110",
                getPositionClasses(platform.position),
                getSizeClasses(platform.size),
                platform.className,
              )}
              data-oid="pmzqldt"
            >
              <platform.icon
                className={cn(
                  "size-auto text-foreground",
                  getIconSizeClasses(platform.iconSize),
                )}
                data-oid="g7_r5-6"
              />
            </div>
          ))}
        </div>
      </Container>
    </div>
  );
};

export default Integration;
